import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, tap } from 'rxjs/operators';
import { EntityDropdownPickerAdapter } from './entity-dropdown-picker.model';

@Component({
  selector: 'app-entity-dropdown-picker',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DropdownModule,
    ButtonModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    IftaLabelModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EntityDropdownPickerComponent),
      multi: true,
    },
  ],
  templateUrl: './entity-dropdown-picker.component.html',
  styleUrls: ['./entity-dropdown-picker.component.css'],
})
export class EntityDropdownPickerComponent<TOption, TValue extends TOption = TOption> implements ControlValueAccessor, OnInit, OnDestroy {
  @Input({ required: true }) adapter!: EntityDropdownPickerAdapter<TOption, TValue>;
  @Input() inputId = 'entity-picker';
  @Input() placeholder = 'Select';
  @Input() label = 'Select';
  @Input() showClear = true;
  @Input() appendTo: 'body' | undefined = 'body';
  @Input() styleClass = 'w-full';
  @Input() panelStyleClass = 'w-full';
  @Input() filterPlaceholder = 'Filter';
  @Input() emptyMessage = 'No results found';
  @Input() columnClass = 'col-12 md:col-6 lg:col-4';
  @Input() addonIconClass = '';
  @Input() recentLimit = 10;
  @Input() searchPageSize = 25;
  @Input() searchMinLength = 1;
  @Input() searchDebounceMs = 250;

  @Output() selectionLoaded = new EventEmitter<TValue | null>();
  @Output() pickerError = new EventEmitter<unknown>();

  options: TOption[] = [];
  selectedValue: TValue | null = null;
  isLoading = false;

  private querySubject = new Subject<string>();
  private querySubscription?: Subscription;
  private onChange: (value: TValue | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  @HostBinding('class')
  get hostClass(): string {
    return `entity-dropdown-picker field ${this.columnClass}`.trim();
  }

  ngOnInit(): void {
    this.querySubscription = this.querySubject
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
        switchMap((query) => this.loadOptionsForQuery(query))
      )
      .subscribe();
  }

  onDropdownShow(): void {
    this.loadRecentOptions();
  }

  onDropdownHide(): void {
    this.querySubject.next('');
  }

  onFilterInput(query: string): void {
    this.querySubject.next(query);
  }

  onSelectionChange(selection: TOption | TValue | null): void {
    this.onTouched();

    if (!selection) {
      this.selectedValue = null;
      this.onChange(null);
      this.selectionLoaded.emit(null);
      this.loadRecentOptions();
      return;
    }

    const id = this.adapter.getId(selection);
    this.isLoading = true;

    const freshSelection$: Observable<TValue> = this.adapter.getFreshById(id).pipe(
      catchError((error): Observable<TValue> => {
        this.pickerError.emit(error);
        return of(selection as TValue);
      }),
      finalize(() => {
        this.isLoading = false;
      })
    );

    freshSelection$.subscribe({
      next: (fresh: TValue) => {
        this.selectedValue = fresh;
        this.upsertOption(fresh);
        this.touchSelection(fresh);
        this.onChange(fresh);
        this.selectionLoaded.emit(fresh);
      },
      error: () => undefined,
    });
  }

  writeValue(value: TValue | null): void {
    this.selectedValue = value;
    if (value) {
      this.upsertOption(value);
    }
  }

  registerOnChange(fn: (value: TValue | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // PrimeNG binds directly to the disabled state below.
    this.disabled = isDisabled;
  }

  disabled = false;

  getLabel(item: TOption | TValue): string {
    return this.adapter.getLabel(item);
  }

  getSecondaryLabel(item: TOption | TValue): string {
    return this.adapter.getSecondaryLabel?.(item) ?? '';
  }

  trackById = (_: number, item: TOption): string | number => this.adapter.getId(item);

  private loadRecentOptions(): void {
    this.isLoading = true;
    this.adapter.loadRecent(this.recentLimit).pipe(
      catchError((error) => {
        this.pickerError.emit(error);
        return of([] as TOption[]);
      }),
      finalize(() => {
        this.isLoading = false;
      })
    ).subscribe({
      next: (recentItems: TOption[]) => {
        this.options = this.mergeSelectedIntoList(recentItems);
      },
      error: () => undefined,
    });
  }

  private loadOptionsForQuery(query: string) {
    const normalizedQuery = query.trim();

    if (normalizedQuery.length < this.searchMinLength) {
      this.loadRecentOptions();
      return of(void 0);
    }

    this.isLoading = true;
    return this.adapter.search(normalizedQuery, 0, this.searchPageSize).pipe(
      catchError((error) => {
        this.pickerError.emit(error);
        return of({ content: [] } as { content: TOption[] });
      }),
      finalize(() => {
        this.isLoading = false;
      }),
      tap((page) => {
        this.options = this.mergeSelectedIntoList(page?.content || []);
      })
    );
  }

  private touchSelection(entity: TValue): void {
    const result = this.adapter.rememberSelection(entity);
    if (result instanceof Promise) {
      void result.catch((error) => this.pickerError.emit(error));
    } else if (result && typeof (result as any).subscribe === 'function') {
      void (result as any).pipe(catchError((error: unknown) => {
        this.pickerError.emit(error);
        return of(void 0);
      })).subscribe();
    }
  }

  private upsertOption(entity: TValue): void {
    const entityId = this.adapter.getId(entity);
    const existingIndex = this.options.findIndex((item) => this.adapter.getId(item) === entityId);
    if (existingIndex >= 0) {
      const nextOptions = [...this.options];
      nextOptions.splice(existingIndex, 1);
      this.options = [entity, ...nextOptions];
      return;
    }

    this.options = [entity, ...this.options];
  }

  private mergeSelectedIntoList(items: TOption[]): TOption[] {
    if (!this.selectedValue) {
      return items;
    }

    const selectedId = this.adapter.getId(this.selectedValue);
    const withoutSelected = items.filter((item) => this.adapter.getId(item) !== selectedId);
    return [this.selectedValue, ...withoutSelected];
  }

  ngOnDestroy(): void {
    this.querySubscription?.unsubscribe();
  }
}
